import { MS_PER_DAY } from './constants';

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isFutureOrToday(date) {
  if (!date) return false;
  const now = new Date();
  return date >= now;
}

export function mapInformationManagerDetails(rawDetails = {}) {
  const metadata = rawDetails.metadata || {};
  const trialBalance = Array.isArray(rawDetails.tb) ? rawDetails.tb : [];
  const checklist = Array.isArray(rawDetails.checklist) ? rawDetails.checklist : [];
  const summary = rawDetails.summary || {};

  const trialBalanceTotals = {};
  trialBalance.forEach(item => {
    const referenceDetails = Array.isArray(item.reference_detail) ? item.reference_detail : [];
    referenceDetails.forEach(referenceItem => {
      const refKey = referenceItem?.ref;
      if (!refKey) return;
      if (!trialBalanceTotals[refKey]) {
        trialBalanceTotals[refKey] = { total: 0, items: [] };
      }
      trialBalanceTotals[refKey].items.push(item);
      const amount = Number(item.closing_amount) || 0;
      trialBalanceTotals[refKey].total += amount;
    });
  });

  const pbcReferenceCounts = {};
  const pbcItems = Array.isArray(rawDetails.pbcitems) ? rawDetails.pbcitems : [];
  pbcItems.forEach(pbcItem => {
    const references = Array.isArray(pbcItem.reference) ? pbcItem.reference : [];
    references.forEach(ref => {
      if (!ref) return;
      if (!pbcReferenceCounts[ref]) {
        pbcReferenceCounts[ref] = { total: 0, completed: 0 };
      }
      pbcReferenceCounts[ref].total += 1;
      if (pbcItem.stage === 2) {
        pbcReferenceCounts[ref].completed += 1;
      }
    });
  });

  const enrichedChecklist = checklist.map(item => {
    const enrichedItem = { ...item };
    enrichedItem.__pbccount = pbcReferenceCounts[item._id] || { total: 0, completed: 0 };
    enrichedItem.__tbtotals = trialBalanceTotals[item.ref] || { total: 0, items: [] };
    return enrichedItem;
  });

  const refList = enrichedChecklist.map(item => ({
    value: item._id,
    name: item.description,
    master_id: item.master_id,
    ref: item.ref,
    location: item.location,
  }));

  return {
    metadata,
    trialBalance,
    checklist: enrichedChecklist,
    summary,
    refList,
    pbcReferenceCounts,
    trialBalanceTotals,
  };
}

export function mapInformationManagerUsers(formData = {}, colorPalette = []) {
  const users = Array.isArray(formData.users) ? formData.users : [];
  return users.map((user, index) => ({
    ...user,
    color: colorPalette[index] || '#BDBDBD',
  }));
}

export function calculateDueDateDays(duedate, msPerDay = MS_PER_DAY) {
  const date = parseDate(duedate);
  if (!date || typeof msPerDay !== 'number' || msPerDay === 0) return '';
  const diff = Math.ceil((date - new Date()) / msPerDay);
  return diff >= 0 ? diff : Math.abs(diff);
}

export function determineDueDateColor(duedate) {
  const date = parseDate(duedate);
  if (!date) return '';
  return isFutureOrToday(date) ? 'lightpurple' : 'lightred';
}

export function determineDueDateTextClass(duedate) {
  const date = parseDate(duedate);
  if (!date) return '';
  return isFutureOrToday(date) ? 'caccounting--text' : 'error--text';
}

export function determineDueDateLabel(duedate) {
  const date = parseDate(duedate);
  if (!date) return '';
  return isFutureOrToday(date) ? 'Days Remaining' : 'Days Overdue';
}

export function resolveStageLabel(stage, labelMap = {}) {
  if (stage === null || stage === undefined) return 'Unknown';
  const key = stage.toString();
  return labelMap[key] || 'Unknown';
}

export function buildRequestUpdatePayload(request = {}) {
  if (!request || typeof request !== 'object') {
    return {};
  }
  const payload = {
    _id: request._id || '',
    description: request.description || '',
    category: request.category || undefined,
    info_owner: request.info_owner || '',
    type: request.type || '',
    response: '',
    urgent: Boolean(request.urgent),
    selectlist: Array.isArray(request.selectlist) ? request.selectlist : [],
    uploadlink: request.uploadlink || '',
    location: request.location,
    reference: Array.isArray(request.reference) ? request.reference : [],
  };

  if (request.duedate) {
    payload.duedate = request.duedate;
  }

  if (request.priority) {
    payload.priority = request.priority;
  }

  return payload;
}

export function resolveLocationName(locationId, locations = []) {
  if (!Array.isArray(locations)) {
    return '';
  }
  const location = locations.find(item => item && item._id === locationId);
  return location ? location.name || '' : '';
}

const REQUEST_FIELD_RULES = [
  { key: 'description', label: 'Description', required: true },
  { key: 'info_owner', label: 'Information Owner', required: true, minLength: 2 },
  { key: 'type', label: 'Type', required: true },
  { key: 'priority', label: 'Priority', required: true },
  { key: 'category', label: 'Classification', required: true },
  { key: 'duedate', label: 'Due Date', required: true },
  { key: 'location', label: 'Location', required: true },
];

function validateFieldValue(value, rule) {
  const { required, minLength, requiresArrayContent, label } = rule;
  const isNullish = value === undefined || value === null;
  const isStringEmpty = typeof value === 'string' && value.trim().length === 0;
  const isArrayEmpty = Array.isArray(value) && value.length === 0;

  if (required && (isNullish || isStringEmpty || isArrayEmpty)) {
    return `${label} is required`;
  }

  if (requiresArrayContent && Array.isArray(value) && value.length === 0) {
    return `${label} is required`;
  }

  if (minLength && typeof value === 'string' && value.trim().length > 0 && value.trim().length < minLength) {
    return `${label} must be at least ${minLength} characters.`;
  }

  return null;
}

export function validateInformationRequest(request = {}) {
  const errors = {};
  let hasError = false;

  REQUEST_FIELD_RULES.forEach(rule => {
    const error = validateFieldValue(request[rule.key], rule);
    if (error) {
      errors[rule.key] = error;
      hasError = true;
    }
  });

  if (request.uploadlink !== undefined && request.uploadlink !== null && typeof request.uploadlink === 'string' && request.uploadlink.trim().length > 0) {
    try {
      const url = new URL(request.uploadlink.trim());
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        errors.uploadlink = 'Upload link must use http:// or https:// protocol';
        hasError = true;
      }
    } catch (err) {
      errors.uploadlink = 'Upload link must be a valid URL starting with http:// or https://';
      hasError = true;
    }
  }

  return { errors, hasError };
}
